/**
 * Box S, the MMM scope (spec §4.2, §5.9, §5.10): every ad account with its live 28-day spend, in D1's OverviewCard and
 * OverviewTable. The admin ticks accounts in local state, and the currency line follows the ticks; nothing is saved
 * until "ยืนยัน scope" (plan R3). An account with no currency has no checkbox and is counted in a note. The warning
 * shows when the confirmed scope differs from the active version's as a set, or its metric does (spec §5.10, T12g).
 *
 * @module components/budget/ScopeCard
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OverviewCard } from '@/components/overview/OverviewBox';
import { Chip, OverviewTable, type OverviewColumn } from '@/components/overview/OverviewTable';
import { PlatformDot } from '@/components/PlatformDot';
import type { OutcomeMetric } from '@/services/budget/budgetContract';
import { isCount, unitsName } from '@/services/budget/outcomeUnits';
import type { ScopeRow } from '@/services/budget/scopeRows';
import type { TFn } from '@/services/channels/channelFormat';
import { DASH, fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';

const BUTTON_CLASS =
  'rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-neutral-50 '
  + 'focus:outline-none focus:ring-2 focus:ring-ink-950 disabled:cursor-not-allowed disabled:opacity-50 print:hidden';

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  [...new Set(a)].sort().join() === [...new Set(b)].sort().join();

const toggled = (ids: ReadonlySet<string>, id: string): ReadonlySet<string> =>
  (ids.has(id) ? new Set([...ids].filter((x) => x !== id)) : new Set([...ids, id]));

/** The Account cell: a checkbox for the admin, the name alone for the others; a row that cannot join says why. */
const AccountCell: React.FC<{ row: ScopeRow; canEdit: boolean; ticked: boolean; onToggle: () => void }> = ({ row, canEdit, ticked, onToggle }) => {
  const { t } = useTranslation();
  const name = row.accountName ?? DASH;
  if (!row.selectable) return <>{name}<Chip text={t('budget.scope.noCurrency')} /></>;
  if (!canEdit) return <>{name}{!row.inScope && <Chip text={t('budget.scope.outOfScope')} />}</>;
  return (
    <label className="inline-flex cursor-pointer items-center gap-2">
      <input type="checkbox" checked={ticked} onChange={onToggle} className="h-4 w-4 accent-ink-950" />
      {name}
    </label>
  );
};

const columnsOf = (t: TFn, cell: (r: ScopeRow) => React.ReactNode): Array<OverviewColumn<ScopeRow>> => [
  { id: 'account', header: t('budget.scope.col.account'), cell },
  {
    id: 'channel', header: t('budget.scope.col.channel'),
    cell: (r) => <span className="inline-flex items-center gap-1.5"><PlatformDot platform={r.platform} />{platformShortLabel(r.platform)}</span>,
  },
  { id: 'id', header: t('budget.scope.col.id'), cell: (r) => <code className="text-xs">{r.externalAccountId}</code> },
  { id: 'spend', header: t('budget.scope.col.spend'), numeric: true, cell: (r) => fmtMoney(r.spend28, r.currency) },
  { id: 'share', header: t('budget.scope.col.share'), numeric: true, cell: (r) => fmtPct(r.share) },
];

/** Check 7 on the ticks, as box 1 words it: one currency passes; none or several fail. */
const CurrencyLine: React.FC<{ currencies: readonly string[] }> = ({ currencies }) => {
  const { t } = useTranslation();
  const pass = currencies.length === 1;
  return (
    <p data-scope-currency data-pass={String(pass)} className="mt-3 text-sm text-neutral-800">
      <span className={pass ? 'text-success-700' : 'text-error-700'}>{pass ? '✓' : '✕'}</span>
      {` ${t('budget.check.currency')} · ${currencies.length > 0 ? currencies.join(' · ') : DASH}`}
    </p>
  );
};

/** "ยืนยัน scope" saves the ticks; never an empty scope, which set_mmm_config refuses. A failure is a fixed line. */
const ConfirmButton: React.FC<{ ids: string[]; onConfirm: (ids: string[]) => Promise<void> }> = ({ ids, onConfirm }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const confirm = async (): Promise<void> => {
    setSaving(true);
    setFailed(false);
    try {
      await onConfirm(ids);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <button type="button" className={BUTTON_CLASS} disabled={saving || ids.length === 0} onClick={() => void confirm()}>
        {saving ? t('budget.saving') : t('budget.scope.confirm')}
      </button>
      {failed && <p role="alert" className="text-sm font-medium text-error-700">{t('budget.saveFailed')}</p>}
    </div>
  );
};

interface Props {
  rows: readonly ScopeRow[];
  canEdit: boolean;
  /** null = nothing confirmed yet. */
  confirmedIds: readonly string[] | null;
  /** The active version's scope; null = no version. */
  versionIds: readonly string[] | null;
  /** The config's metric, or revenue before a confirm (plan R3). */
  metric: OutcomeMetric;
  /** The active version's metric; null = no version. */
  versionMetric: OutcomeMetric | null;
  onConfirm: (ids: string[]) => Promise<void>;
}

export const ScopeCard: React.FC<Props> = ({ rows, canEdit, confirmedIds, versionIds, metric, versionMetric, onConfirm }) => {
  const { t } = useTranslation();
  const [edit, setEdit] = useState<{ seed: string; ids: ReadonlySet<string> } | null>(null);
  const scope = rows.filter((r) => r.inScope).map((r) => r.id);
  const seed = scope.join();
  // Ticks made against another scope (a saved one, another workspace's) give way to the rows'.
  const ticked = edit !== null && edit.seed === seed ? edit.ids : new Set(scope);
  const tickedRows = rows.filter((r) => r.selectable && ticked.has(r.id));
  // Sorted, as get_mmm_readiness orders check 7's currencies.
  const currencies = [...new Set(tickedRows.flatMap((r) => (r.currency === null ? [] : [r.currency])))].sort();
  const unknown = rows.filter((r) => !r.selectable).length;
  // Check 6's "changed" (readinessRules): a confirmed config whose scope or metric is not the version's (T12g).
  const changed = confirmedIds !== null && versionIds !== null
    && (!sameSet(confirmedIds, versionIds) || (versionMetric !== null && metric !== versionMetric));

  const cell = (r: ScopeRow): React.ReactNode => (
    <AccountCell row={r} canEdit={canEdit} ticked={ticked.has(r.id)} onToggle={() => setEdit({ seed, ids: toggled(ticked, r.id) })} />
  );

  return (
    <OverviewCard id="budget-s" title={t('budget.scope.title')}>
      <p className="-mt-2 mb-2 text-xs text-neutral-700">{t('budget.scope.subtitle')}</p>
      <p data-scope-intro className="mb-3 text-sm text-neutral-800">
        {isCount(metric) ? t('budget.scope.introCount', { units: unitsName(metric, t) }) : t('budget.scope.intro', { metric })}
      </p>
      <OverviewTable columns={columnsOf(t, cell)} rows={rows} rowKey={(r) => r.id} />
      <CurrencyLine currencies={currencies} />
      {unknown > 0 && <p data-scope-note className="mt-1 text-xs text-neutral-700">{t('budget.scope.noCurrencyNote', { n: unknown })}</p>}
      {changed && (
        <p data-scope-changed className="mt-3 rounded-lg border border-warning-700 px-3 py-2 text-sm text-warning-700">
          {t('budget.scope.changed', { n: new Set(versionIds).size })}
        </p>
      )}
      {canEdit && <ConfirmButton ids={tickedRows.map((r) => r.id)} onConfirm={onConfirm} />}
    </OverviewCard>
  );
};
